/**
 * Read the two airports out of an airports-briefing response (#451 review).
 *
 * The `flightRoute` LineString the server appends to
 * `/api/notam-geojson/airports` carries the departure as its first
 * coordinate and the destination as its last. Two callers read it — the
 * briefing screen, which also wants the alternates, and the simulator's
 * coordinate lookup — and they had drifted apart within one PR: one
 * skipped a malformed line silently, the other threw, and neither checked
 * that `coordinates` was present at all, so a `LineString` without one
 * raised a TypeError instead of failing cleanly.
 */
import type { FlightRoute, NotamGeoJson } from '@/types/notam';

/** The two airports, or null when the response carries no usable route line. */
export function flightRouteFromGeoJson(
  geojson: NotamGeoJson,
  departure: string,
  destination: string
): FlightRoute | null {
  const routeFeature = geojson.features?.find(
    f => f.properties?.featureType === 'flightRoute'
  );
  if (!routeFeature || routeFeature.geometry?.type !== 'LineString') return null;

  // `coordinates` is typed as present, but this is a server response: a
  // LineString without one is a 200 the client must survive, not crash on.
  const coords = routeFeature.geometry.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;

  return {
    departure: { code: departure, coords: coords[0] as [number, number] },
    destination: { code: destination, coords: coords[coords.length - 1] as [number, number] }
  };
}
